import React, { useState, useMemo, useEffect } from "react";
import {
  Grid,
  Paper,
  Text,
  Title,
  Group,
  Stack,
  TextInput,
  Select,
  Button,
  Badge,
  ActionIcon,
  Textarea,
  Box,
  Divider,
  Modal,
  Tooltip,
  Accordion,
  NumberInput,
} from "@mantine/core";
import {
  MantineReactTable,
  useMantineReactTable,
  MRT_ColumnDef,
} from "mantine-react-table";
import { DatePickerInput } from "@mantine/dates";
import dayjs from "dayjs";
import { styleTableMantine } from "../../../_base/_const/_constVar";
import { _localization } from "../../../config/location";
import {
  IconEye,
  IconDownload,
  IconSearch,
  IconPhone,
  IconCalendar,
  IconHeadset,
  IconNotes,
  IconX,
} from "@tabler/icons-react";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import BreadCrumb from "../../../_base/component/_layout/_breadcrumb";
import { getListTradeInOrder, modifyTradeInOrder } from "../../../api/ApiTradeIn";
import { formatDateTransfer } from "../../../common/FormatDate/FormatDate";
import { TblTradeInOrder } from "../../../model/TblTradeIn";
import { apiPOS } from "../../../library/axios";
import { getDataProvice } from "../../../api/ApiAddress";

const mapDbStatusToUi = (status: string | null): string => {
  if (!status) return "Mới";
  switch (status) {
    case "P":
      return "Mới";
    case "S":
      return "Đã liên hệ";
    case "C":
      return "Không quan tâm";
    default:
      return status;
  }
};


export const TradeInOverviewV2 = () => {
  const [requests, setRequests] = useState<TblTradeInOrder[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [modalOpened, setModalOpened] = useState(false);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 20,
  });
  const [hasInitializedSelection, setHasInitializedSelection] = useState(false);
  const [stats, setStats] = useState({
    totalAll: 0,
    totalToday: 0,
    totalPending: 0,
    totalSuccess: 0,
    totalCancel: 0,
  });
  
  // States for filter inputs (draft state)
  const [searchInput, setSearchInput] = useState("");
  const [statusInput, setStatusInput] = useState<string>("Tất cả");
  const [oldDeviceTypeInput, setOldDeviceTypeInput] = useState<string>("Tất cả");
  const [newDeviceNeedInput, setNewDeviceNeedInput] = useState<string>("Tất cả");
  const [fromDateInput, setFromDateInput] = useState<Date | null>(null);
  const [toDateInput, setToDateInput] = useState<Date | null>(null);
  const [orgIdInput, setOrgIdInput] = useState<string>("Tất cả");
  const [provinceIdInput, setProvinceIdInput] = useState<string>("Tất cả");

  // Applied filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("Tất cả");
  const [oldDeviceTypeFilter, setOldDeviceTypeFilter] = useState<string>("Tất cả");
  const [newDeviceNeedFilter, setNewDeviceNeedFilter] = useState<string>("Tất cả");
  const [fromDateFilter, setFromDateFilter] = useState<Date | null>(null);
  const [toDateFilter, setToDateFilter] = useState<Date | null>(null);
  const [orgIdFilter, setOrgIdFilter] = useState<string>("Tất cả");
  const [provinceIdFilter, setProvinceIdFilter] = useState<string>("Tất cả");

  // Metadata lists for Select options
  const [provincesList, setProvincesList] = useState<any[]>([]);
  const [branchesList, setBranchesList] = useState<any[]>([]);

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setStatusFilter(statusInput);
    setOldDeviceTypeFilter(oldDeviceTypeInput);
    setNewDeviceNeedFilter(newDeviceNeedInput);
    setFromDateFilter(fromDateInput);
    setToDateFilter(toDateInput);
    setOrgIdFilter(orgIdInput);
    setProvinceIdFilter(provinceIdInput);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const typeParam =
        newDeviceNeedFilter === "Có đổi máy mới"
          ? "N"
          : newDeviceNeedFilter === "Chỉ bán máy cũ"
          ? "O"
          : null;
      const statusParam =
        statusFilter === "Tất cả"
          ? null
          : statusFilter === "Mới"
          ? "P"
          : statusFilter === "Đang tư vấn"
          ? "P"
          : statusFilter === "Đã liên hệ"
          ? "S"
          : statusFilter === "Không quan tâm"
          ? "C"
          : null;

      let fromDateParam: string | null = null;
      let toDateParam: string | null = null;

      if (fromDateFilter) {
        fromDateParam = dayjs(fromDateFilter).format("YYYY-MM-DD[T]00:00:00[Z]");
      }
      if (toDateFilter) {
        toDateParam = dayjs(toDateFilter).format("YYYY-MM-DD[T]00:00:00[Z]");
      }

      const response = await getListTradeInOrder({
        take: pagination.pageSize,
        skip: pagination.pageIndex * pagination.pageSize,
        keySearch: searchTerm || null,
        type: typeParam,
        status: statusParam,
        fromDate: fromDateParam,
        toDate: toDateParam,
        orgId: orgIdFilter === "Tất cả" ? null : orgIdFilter,
        provinceId: provinceIdFilter === "Tất cả" ? null : Number(provinceIdFilter),
      });

      if (response && response.success && Array.isArray(response.data)) {
        setRequests(response.data);
        setTotal(response.totalCount || response.data.length);
      } else {
        setRequests([]);
        setTotal(0);
      }
    } catch (error) {
      console.error("Error fetching trade in orders:", error);
      NotificationExtension.Fails("Không thể tải danh sách yêu cầu!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    searchTerm,
    statusFilter,
    newDeviceNeedFilter,
    fromDateFilter,
    toDateFilter,
    orgIdFilter,
    provinceIdFilter,
  ]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [
    searchTerm,
    statusFilter,
    newDeviceNeedFilter,
    fromDateFilter,
    toDateFilter,
    orgIdFilter,
    provinceIdFilter,
  ]);

  useEffect(() => {
    if (requests.length > 0 && !hasInitializedSelection) {
      setSelectedId(requests[0].id);
      setHasInitializedSelection(true);
    }
  }, [requests, hasInitializedSelection]);

  const fetchStats = async () => {
    try {
      const resAll = await getListTradeInOrder({ take: 1, skip: 0 });
      const totalAllVal = resAll?.totalCount || 0;

      const resPending = await getListTradeInOrder({ take: 1, skip: 0, status: "P" });
      const totalPendingVal = resPending?.totalCount || 0;

      const resSuccess = await getListTradeInOrder({ take: 1, skip: 0, status: "S" });
      const totalSuccessVal = resSuccess?.totalCount || 0;

      const resCancel = await getListTradeInOrder({ take: 1, skip: 0, status: "C" });
      const totalCancelVal = resCancel?.totalCount || 0;

      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      
      const resTodayCheck = await getListTradeInOrder({ take: 100, skip: 0 });
      let todayCount = 0;
      if (resTodayCheck && Array.isArray(resTodayCheck.data)) {
        todayCount = resTodayCheck.data.filter((item: any) => {
          if (!item.creationDate) return false;
          return item.creationDate.startsWith(todayStr);
        }).length;
      }

      setStats({
        totalAll: totalAllVal,
        totalToday: todayCount,
        totalPending: totalPendingVal,
        totalSuccess: totalSuccessVal,
        totalCancel: totalCancelVal,
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    const loadProvinces = async () => {
      try {
        const res = await getDataProvice("Skip=0&Take=250");
        if (res && res.success && Array.isArray(res.data)) {
          setProvincesList(res.data);
        }
      } catch (err) {
        console.error("Failed to load provinces:", err);
      }
    };

    const loadBranches = async () => {
      try {
        const url = `/api-pos/api/v1/TblDmInventory/get-select-branch?auth=false`;
        const res = await apiPOS.get(url);
        if (res && res.data && res.data.success && Array.isArray(res.data.data)) {
          const filtered = res.data.data.filter((item: any) => item.att2 != null && item.attr6 === "HACOM");
          setBranchesList(filtered);
        }
      } catch (err) {
        console.error("Failed to load branches:", err);
      }
    };

    loadProvinces();
    loadBranches();
  }, []);

  // Filter requests
  const filteredRequests = useMemo(() => {
    return requests.filter((r) => {
      // 1. Search term filter
      const matchesSearch =
        !searchTerm ||
        (r.cusName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (r.mobileNumber || "").includes(searchTerm) ||
        (r.productName || "").toLowerCase().includes(searchTerm.toLowerCase());
      
      // 2. Status filter
      const matchesStatus =
        statusFilter === "Tất cả" || mapDbStatusToUi(r.status) === statusFilter;

      // 3. Old device type filter (Loại 1, 2, 3)
      let matchesOldDeviceType = true;
      if (oldDeviceTypeFilter !== "Tất cả") {
        matchesOldDeviceType = r.productCondition === oldDeviceTypeFilter;
      }

      // 4. New device need filter
      let matchesNewDeviceNeed = true;
      const hasUpgrade = !!r.productUpgradeName || !!r.itemNameUpdate;
      if (newDeviceNeedFilter === "Có đổi máy mới") {
        matchesNewDeviceNeed = hasUpgrade;
      } else if (newDeviceNeedFilter === "Chỉ bán máy cũ") {
        matchesNewDeviceNeed = !hasUpgrade;
      }

      return (
        matchesSearch &&
        matchesStatus &&
        matchesOldDeviceType &&
        matchesNewDeviceNeed
      );
    });
  }, [
    requests,
    searchTerm,
    statusFilter,
    oldDeviceTypeFilter,
    newDeviceNeedFilter,
  ]);

  // Mantine React Table Columns
  const columns = useMemo<MRT_ColumnDef<TblTradeInOrder>[]>(
    () => [
      {
        id: "index",
        header: "#",
        size: 50,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: "creationDate",
        header: "Thời gian",
        size: 130,
        Cell: ({ cell }) => {
          const val = cell.getValue<string>();
          return val ? formatDateTransfer(val) : "";
        },
      },
      {
        accessorKey: "cusName",
        header: "Khách hàng",
        size: 140,
        Cell: ({ cell }) => (
          <Text size="sm" fw={500}>
            {cell.getValue<string>()}
          </Text>
        ),
      },
      {
        accessorKey: "mobileNumber",
        header: "SĐT",
        size: 110,
      },
      {
        accessorKey: "address",
        header: "Địa chỉ",
        size: 180,
        Cell: ({ cell }) => (
          <Text size="sm" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
            {cell.getValue<string>() || ""}
          </Text>
        ),
      },
      {
        accessorKey: "attribute2",
        header: "Chi nhánh nhận",
        size: 200,
        Cell: ({ cell }) => (
          <Text size="sm" style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
            {cell.getValue<string>() || ""}
          </Text>
        ),
      },
      {
        accessorKey: "productName",
        header: "Máy cũ",
        size: 150,
        Cell: ({ row }) => (
          <Box>
            <Text size="sm">{row.original.productName}</Text>
            <Text size="xs" c="dimmed">
              ({row.original.productCondition || "Loại 1"})
            </Text>
          </Box>
        ),
      },
      {
        accessorKey: "productUpgradeName",
        header: "Máy mới",
        size: 150,
        Cell: ({ row }) => {
          const newName = row.original.productUpgradeName || row.original.itemNameUpdate;
          const newPrice = row.original.priceUpgrade || row.original.itemPriceUpdate || 0;
          return newPrice > 0 ? (
            <Box>
              <Text size="sm">{newName || "Sản phẩm lên đời"}</Text>
              <Text size="xs" c="dimmed">
                ({newPrice.toLocaleString()}đ)
              </Text>
            </Box>
          ) : (
            <Text size="sm" c="dimmed">
              Chỉ bán máy cũ
            </Text>
          );
        },
      },
      {
        id: "estimatedDiff",
        header: "Tiền bù dự kiến",
        size: 140,
        Cell: ({ row }) => {
          const rowData = row.original;
          const newPrice = rowData.priceUpgrade || rowData.itemPriceUpdate || 0;
          const oldEstimate = rowData.estimate || 0;
          const supportAmount = rowData.supportAmount || 0;

          if (newPrice === 0) {
            return (
              <Text size="sm" fw={600} c="green">
                Khách nhận: {oldEstimate.toLocaleString()}đ
              </Text>
            );
          }

          const diff = rowData.priceDifference !== null && rowData.priceDifference !== undefined
            ? rowData.priceDifference
            : (newPrice - oldEstimate - supportAmount);

          if (diff > 0) {
            return (
              <Text size="sm" fw={600} c="blue">
                Khách bù: +{diff.toLocaleString()}đ
              </Text>
            );
          } else {
            return (
              <Text size="sm" fw={600} c="green">
                Khách nhận: {Math.abs(diff).toLocaleString()}đ
              </Text>
            );
          }
        },
      },
      {
        accessorKey: "status",
        header: "Trạng thái",
        size: 120,
        Cell: ({ cell }) => getStatusBadge(cell.getValue<string>()),
      },
      {
        id: "actions",
        header: "Thao tác",
        size: 80,
        Cell: ({ row }) => (
          <Tooltip label="Xem chi tiết">
            <ActionIcon
              variant="light"
              color="blue"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedId(row.original.id);
                setModalOpened(true);
              }}
            >
              <IconEye size={18} />
            </ActionIcon>
          </Tooltip>
        ),
      }
    ],
    []
  );

  const table = useMantineReactTable({
    columns,
    data: filteredRequests,
    enableColumnResizing: false,
    enableSorting: true,
    enableColumnActions: false,
    enableTopToolbar: false,
    enablePagination: true,
    localization: _localization,
    rowCount: total,
    manualPagination: true,
    onPaginationChange: setPagination,
    enableColumnPinning: true,
    initialState: {
      columnPinning: {
        left: ["index"],
        right: ["actions"],
      },
    },
    state: {
      isLoading: loading,
      pagination,
    },
    mantineTableContainerProps: {
      style: {
        height: "calc(100vh - 420px)",
      },
    },
    mantineTableBodyRowProps: ({ row }) => ({
      style: {
        backgroundColor:
          selectedId === row.original.id
            ? "var(--mantine-color-blue-0)"
            : undefined,
      },
    }),
    mantineTableBodyCellProps: () => ({
      style: {
        ...styleTableMantine,
        padding: "12px 10px",
      },
    }),
    mantineTableHeadCellProps: () => ({
      style: {
        padding: "12px 10px",
      },
    }),
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      radius: "md",
      size: "md",
      rowsPerPageOptions: ["5", "10", "20", "50"],
    },
    paginationDisplayMode: "pages",
  });

  // Detail panel editing state
  const selectedItem = requests.find((r) => r.id === selectedId) || null;
  const [editStatus, setEditStatus] = useState<any>(mapDbStatusToUi(selectedItem?.status || null));
  const [editInternalNote, setEditInternalNote] = useState(selectedItem?.note || "");


  useEffect(() => {
    if (selectedItem) {
      setEditStatus(mapDbStatusToUi(selectedItem.status));
      setEditInternalNote(selectedItem.note || "");
    }
  }, [selectedId, selectedItem]);

  const handleSaveDetail = async () => {
    if (selectedId === null || !selectedItem) return;
    try {
      const mappedStatus =
        editStatus === "Mới"
          ? "P"
          : editStatus === "Đang tư vấn"
          ? "P"
          : editStatus === "Đã liên hệ"
          ? "S"
          : editStatus === "Không quan tâm"
          ? "C"
          : editStatus;

      const updatedItem = {
        ...selectedItem,
        status: mappedStatus,
        note: editInternalNote,
      };

      const response = await modifyTradeInOrder(updatedItem);
      if (response && response.success) {
        await fetchData();
        await fetchStats();
        NotificationExtension.Success("Cập nhật thông tin yêu cầu thành công!");
        setModalOpened(false);
      } else {
        NotificationExtension.Fails("Cập nhật thông tin yêu cầu thất bại!");
      }
    } catch (error) {
      console.error("Error saving detail:", error);
      NotificationExtension.Fails("Không thể lưu thông tin chi tiết!");
    }
  };





  const getStatusBadge = (status: string) => {
    const uiStatus = mapDbStatusToUi(status);
    switch (uiStatus) {
      case "Mới":
        return <Badge color="blue">Mới</Badge>;
      case "Đã liên hệ":
        return <Badge color="green">Đã liên hệ</Badge>;
      case "Đang tư vấn":
        return <Badge color="orange">Đang tư vấn</Badge>;
      case "Không quan tâm":
        return <Badge color="red">Không quan tâm</Badge>;
      default:
        return <Badge color="gray">{uiStatus}</Badge>;
    }
  };

  // Calculations for detail card
  const newDevicePrice = selectedItem?.priceUpgrade || selectedItem?.itemPriceUpdate || 0;
  const oldDeviceEstimatedPrice = selectedItem?.estimate || 0;
  const supportAmount = selectedItem?.supportAmount || 0;
  const supportRate = oldDeviceEstimatedPrice > 0 ? Math.round((supportAmount / oldDeviceEstimatedPrice) * 100) : 0;
  const estimatedDiff = selectedItem?.priceDifference !== null && selectedItem?.priceDifference !== undefined
    ? selectedItem.priceDifference
    : (newDevicePrice - oldDeviceEstimatedPrice - supportAmount);
  const otherAdjustment = selectedItem?.priceDifference !== null && selectedItem?.priceDifference !== undefined
    ? selectedItem.priceDifference - (newDevicePrice - oldDeviceEstimatedPrice - supportAmount)
    : 0;

  return (
    <Box p="md" style={{ width: "100%", boxSizing: "border-box" }}>
      <Group gap={0} justify="space-between" mb={10}>
        <BreadCrumb />
      </Group>
      {/* Top statistics Row */}
      <Grid mb="lg">
        <Grid.Col span={{ base: 12, sm: 6, md: 2.4 }}>
          <Paper p="md" withBorder radius="md">
            <Group justify="space-between">
              <Stack gap={2}>
                <Text size="xs" c="dimmed">
                  Tổng yêu cầu
                </Text>
                <Text size="xl" fw={700}>
                  {stats.totalAll.toLocaleString()}
                </Text>
                <Text size="xs" c="dimmed">
                  Tất cả thời gian
                </Text>
              </Stack>
              <Paper
                p="xs"
                radius="md"
                bg="blue.0"
                style={{ display: "flex", alignItems: "center" }}
              >
                <IconNotes size={24} color="#228be6" />
              </Paper>
            </Group>
          </Paper>
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6, md: 2.4 }}>
          <Paper p="md" withBorder radius="md">
            <Group justify="space-between">
              <Stack gap={2}>
                <Text size="xs" c="dimmed">
                  Yêu cầu hôm nay
                </Text>
                <Text size="xl" fw={700}>
                  {stats.totalToday.toLocaleString()}
                </Text>
                <Text size="xs" c="dimmed">
                  Ngày {new Date().toLocaleDateString("vi-VN")}
                </Text>
              </Stack>
              <Paper
                p="xs"
                radius="md"
                bg="green.0"
                style={{ display: "flex", alignItems: "center" }}
              >
                <IconCalendar size={24} color="#40c057" />
              </Paper>
            </Group>
          </Paper>
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6, md: 2.4 }}>
          <Paper p="md" withBorder radius="md">
            <Group justify="space-between">
              <Stack gap={2}>
                <Text size="xs" c="dimmed">
                  Đang tư vấn
                </Text>
                <Text size="xl" fw={700}>
                  {stats.totalPending.toLocaleString()}
                </Text>
                <Text size="xs" c="dimmed">
                  Chờ liên hệ
                </Text>
              </Stack>
              <Paper
                p="xs"
                radius="md"
                bg="orange.0"
                style={{ display: "flex", alignItems: "center" }}
              >
                <IconHeadset size={24} color="#fd7e14" />
              </Paper>
            </Group>
          </Paper>
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6, md: 2.4 }}>
          <Paper p="md" withBorder radius="md">
            <Group justify="space-between">
              <Stack gap={2}>
                <Text size="xs" c="dimmed">
                  Đã liên hệ
                </Text>
                <Text size="xl" fw={700}>
                  {stats.totalSuccess.toLocaleString()}
                </Text>
                <Text size="xs" c="dimmed">
                  Tất cả thời gian
                </Text>
              </Stack>
              <Paper
                p="xs"
                radius="md"
                bg="violet.0"
                style={{ display: "flex", alignItems: "center" }}
              >
                <IconPhone size={24} color="#7950f2" />
              </Paper>
            </Group>
          </Paper>
        </Grid.Col>

        <Grid.Col span={{ base: 12, sm: 6, md: 2.4 }}>
          <Paper p="md" withBorder radius="md">
            <Group justify="space-between">
              <Stack gap={2}>
                <Text size="xs" c="dimmed">
                  Không quan tâm
                </Text>
                <Text size="xl" fw={700}>
                  {stats.totalCancel.toLocaleString()}
                </Text>
                <Text size="xs" c="dimmed">
                  Tất cả thời gian
                </Text>
              </Stack>
              <Paper
                p="xs"
                radius="md"
                bg="red.0"
                style={{ display: "flex", alignItems: "center" }}
              >
                <IconX size={24} color="#fa5252" />
              </Paper>
            </Group>
          </Paper>
        </Grid.Col>
      </Grid>

      {/* Main Grid: Left (Tables, Charts) */}
      <Grid>
        {/* Left column - Tables and charts */}
        <Grid.Col span={12}>
          <Stack gap="lg">
            {/* Table card */}
            <Paper p="md" withBorder radius="md">
              <Title order={5} mb="md">
                Yêu cầu tư vấn mới nhất
              </Title>

              {/* Filters bar */}
              <Group mb="md" justify="space-between" align="flex-end">
                <Group gap="xs" style={{ flexGrow: 1 }} align="flex-end">
                  <Box style={{ width: 200 }}>
                    <TextInput
                      placeholder="Tìm kiếm tên, SĐT, máy cũ..."
                      leftSection={<IconSearch size={16} />}
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.currentTarget.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSearch();
                        }
                      }}
                      size="sm"
                    />
                  </Box>
                  <Box style={{ width: 150 }}>
                    <Select
                      data={["Tất cả", "Mới", "Đã liên hệ", "Đang tư vấn", "Không quan tâm"]}
                      value={statusInput}
                      onChange={(val) => setStatusInput(val || "Tất cả")}
                      size="sm"
                    />
                  </Box>
                  <Box style={{ width: 130 }}>
                    <DatePickerInput
                      placeholder="Từ ngày"
                      value={fromDateInput}
                      onChange={setFromDateInput}
                      size="sm"
                      clearable
                      valueFormat="DD/MM/YYYY"
                    />
                  </Box>
                  <Box style={{ width: 130 }}>
                    <DatePickerInput
                      placeholder="Đến ngày"
                      value={toDateInput}
                      onChange={setToDateInput}
                      size="sm"
                      clearable
                      valueFormat="DD/MM/YYYY"
                    />
                  </Box>
                  {/* <Box style={{ width: 180 }}>
                    <Text size="xs" fw={600} mb={5} c="gray.7">
                      Chi nhánh
                    </Text>
                    <Select
                      data={[
                        { label: "Tất cả", value: "Tất cả" },
                        ...branchesList.map((branch) => ({
                          label: branch.addressNew || branch.att1,
                          value: branch.att2,
                        })),
                      ]}
                      value={orgIdInput}
                      onChange={(val) => setOrgIdInput(val || "Tất cả")}
                      searchable
                      clearable
                      size="sm"
                    />
                  </Box>
                  <Box style={{ width: 160 }}>
                    <Text size="xs" fw={600} mb={5} c="gray.7">
                      Tỉnh/Thành phố
                    </Text>
                    <Select
                      data={[
                        { label: "Tất cả", value: "Tất cả" },
                        ...provincesList.map((p) => ({
                          label: p.provinceName,
                          value: p.provinceId.toString(),
                        })),
                      ]}
                      value={provinceIdInput}
                      onChange={(val) => setProvinceIdInput(val || "Tất cả")}
                      searchable
                      clearable
                      size="sm"
                    />
                  </Box> */}
                  <Button
                    color="blue"
                    leftSection={<IconSearch size={16} />}
                    onClick={handleSearch}
                    size="sm"
                  >
                    Tìm kiếm
                  </Button>
                  {(searchInput ||
                    statusInput !== "Tất cả" ||
                    oldDeviceTypeInput !== "Tất cả" ||
                    newDeviceNeedInput !== "Tất cả" ||
                    fromDateInput !== null ||
                    toDateInput !== null ||
                    orgIdInput !== "Tất cả" ||
                    provinceIdInput !== "Tất cả" ||
                    searchTerm ||
                    statusFilter !== "Tất cả" ||
                    oldDeviceTypeFilter !== "Tất cả" ||
                    newDeviceNeedFilter !== "Tất cả" ||
                    fromDateFilter !== null ||
                    toDateFilter !== null ||
                    orgIdFilter !== "Tất cả" ||
                    provinceIdFilter !== "Tất cả") && (
                    <Button
                      variant="subtle"
                      color="gray"
                      onClick={() => {
                        setSearchInput("");
                        setSearchTerm("");
                        setStatusInput("Tất cả");
                        setStatusFilter("Tất cả");
                        setOldDeviceTypeInput("Tất cả");
                        setOldDeviceTypeFilter("Tất cả");
                        setNewDeviceNeedInput("Tất cả");
                        setNewDeviceNeedFilter("Tất cả");
                        setFromDateInput(null);
                        setFromDateFilter(null);
                        setToDateInput(null);
                        setToDateFilter(null);
                        setOrgIdInput("Tất cả");
                        setOrgIdFilter("Tất cả");
                        setProvinceIdInput("Tất cả");
                        setProvinceIdFilter("Tất cả");
                      }}
                      size="sm"
                    >
                      Xóa bộ lọc
                    </Button>
                  )}
                </Group>

              </Group>

              {/* Data Table */}
              <MantineReactTable table={table} />
            </Paper>
           </Stack>
        </Grid.Col>

      </Grid>

      <Modal
        opened={modalOpened}
        onClose={() => {
          setModalOpened(false);
          setSelectedId(null);
        }}
        title={<Title order={5} fw={700}>Chi tiết yêu cầu</Title>}
        size="sm"
        centered
        styles={{
          content: {
            width: "550px",
            minWidth: "320px",
            maxWidth: "95vw",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          },
          body: {
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            padding: 0,
          }
        }}
      >
        {selectedItem && (
          <Box style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", height: "100%" }}>
            <Box style={{ flex: 1, overflowY: "auto", padding: "16px" }}>
              <Stack gap="md">
                {/* Customer Info */}
                <Box>
                  <Group mb="xs" gap="xs">
                    <Text fw={600} size="sm" style={{ borderLeft: "3px solid #228be6", paddingLeft: "8px" }}>
                      Thông tin khách hàng
                    </Text>
                  </Group>
                  <Grid gutter="xs" style={{ fontSize: "14px" }}>
                    <Grid.Col span={4}><Text size="sm" c="dimmed">Họ tên:</Text></Grid.Col>
                    <Grid.Col span={8}><Text size="sm" fw={500}>{selectedItem.cusName}</Text></Grid.Col>

                    <Grid.Col span={4}><Text size="sm" c="dimmed">SĐT:</Text></Grid.Col>
                    <Grid.Col span={8}><Text size="sm">{selectedItem.mobileNumber}</Text></Grid.Col>

                    <Grid.Col span={4}><Text size="sm" c="dimmed">Email:</Text></Grid.Col>
                    <Grid.Col span={8}><Text size="sm">{selectedItem.cusEmail || "Chưa có"}</Text></Grid.Col>

                    <Grid.Col span={4}><Text size="sm" c="dimmed">Thời gian:</Text></Grid.Col>
                    <Grid.Col span={8}><Text size="sm">{selectedItem.creationDate ? formatDateTransfer(selectedItem.creationDate) : ""}</Text></Grid.Col>

                    <Grid.Col span={4}><Text size="sm" c="dimmed">Địa chỉ khách:</Text></Grid.Col>
                    <Grid.Col span={8}><Text size="sm">{selectedItem.address || "Chưa có"}</Text></Grid.Col>

                    <Grid.Col span={4}><Text size="sm" c="dimmed">Chi nhánh nhận:</Text></Grid.Col>
                    <Grid.Col span={8}><Text size="sm">{selectedItem.attribute2 || "Chưa chọn"}</Text></Grid.Col>

                    <Grid.Col span={4}><Text size="sm" c="dimmed">Ghi chú:</Text></Grid.Col>
                    <Grid.Col span={8}><Text size="sm" style={{ fontStyle: "italic" }}>{selectedItem.description || "Không có ghi chú"}</Text></Grid.Col>
                  </Grid>
                </Box>

                {/* Old device info */}
                <Box>
                  <Group mb="xs" gap="xs">
                    <Text fw={600} size="sm" style={{ borderLeft: "3px solid #40c057", paddingLeft: "8px" }}>
                      Thông tin máy cũ
                    </Text>
                  </Group>
                  <Grid gutter="xs" style={{ fontSize: "14px" }}>
                    <Grid.Col span={4}><Text size="sm" c="dimmed">Sản phẩm:</Text></Grid.Col>
                    <Grid.Col span={8}><Text size="sm">{selectedItem.productName}</Text></Grid.Col>

                    <Grid.Col span={4}><Text size="sm" c="dimmed">Tình trạng:</Text></Grid.Col>
                    <Grid.Col span={8}><Text size="sm">{selectedItem.productCondition || "Loại 1"}</Text></Grid.Col>

                    <Grid.Col span={4}><Text size="sm" c="dimmed">Giá thu dự kiến:</Text></Grid.Col>
                    <Grid.Col span={8}><Text size="sm" fw={500} c="green">{(selectedItem.estimate || 0).toLocaleString()}đ</Text></Grid.Col>
                  </Grid>
                </Box>

                {/* New device info */}
                {newDevicePrice > 0 && (
                  <Box>
                    <Group mb="xs" gap="xs">
                      <Text fw={600} size="sm" style={{ borderLeft: "3px solid #7950f2", paddingLeft: "8px" }}>
                        Thông tin máy mới
                      </Text>
                    </Group>
                    <Grid gutter="xs" style={{ fontSize: "14px" }}>
                      <Grid.Col span={4}><Text size="sm" c="dimmed">Sản phẩm:</Text></Grid.Col>
                      <Grid.Col span={8}><Text size="sm">{selectedItem.productUpgradeName || selectedItem.itemNameUpdate || "-"}</Text></Grid.Col>

                      <Grid.Col span={4}><Text size="sm" c="dimmed">Giá bán:</Text></Grid.Col>
                      <Grid.Col span={8}><Text size="sm" fw={500}>{((selectedItem.priceUpgrade || selectedItem.itemPriceUpdate) || 0).toLocaleString()}đ</Text></Grid.Col>
                    </Grid>
                  </Box>
                )}

                {/* Estimation calculation */}
                <Paper p="sm" bg="gray.0" radius="sm">
                  {newDevicePrice === 0 ? (
                    <Grid gutter="xs" style={{ fontSize: "14px" }}>
                      <Grid.Col span={6}><Text size="sm">Giá thu máy cũ:</Text></Grid.Col>
                      <Grid.Col span={6} style={{ textAlign: "right" }}>
                        <Text size="sm" fw={600}>{oldDeviceEstimatedPrice.toLocaleString()}đ</Text>
                      </Grid.Col>

                      <Grid.Col span={6}><Text size="sm">Hỗ trợ lên đời:</Text></Grid.Col>
                      <Grid.Col span={6} style={{ textAlign: "right" }}>
                        <Text size="sm" c="dimmed">0đ (Chỉ thu cũ)</Text>
                      </Grid.Col>

                      <Grid.Col span={12}>
                        <Divider my="xs" />
                      </Grid.Col>

                      <Grid.Col span={6}><Text size="sm" fw={700} c="green">Khách nhận tiền mặt:</Text></Grid.Col>
                      <Grid.Col span={6} style={{ textAlign: "right" }}>
                        <Text size="md" fw={700} c="green">
                          {oldDeviceEstimatedPrice.toLocaleString()}đ
                        </Text>
                      </Grid.Col>
                    </Grid>
                  ) : (
                    <Grid gutter="xs" style={{ fontSize: "14px" }}>
                      <Grid.Col span={6}><Text size="sm">Giá máy mới:</Text></Grid.Col>
                      <Grid.Col span={6} style={{ textAlign: "right" }}><Text size="sm">{newDevicePrice.toLocaleString()}đ</Text></Grid.Col>

                      <Grid.Col span={6}><Text size="sm">Giá thu máy cũ:</Text></Grid.Col>
                      <Grid.Col span={6} style={{ textAlign: "right" }}><Text size="sm">- {oldDeviceEstimatedPrice.toLocaleString()}đ</Text></Grid.Col>

                      <Grid.Col span={6}><Text size="sm">Hỗ trợ lên đời ({supportRate}%):</Text></Grid.Col>
                      <Grid.Col span={6} style={{ textAlign: "right" }}><Text size="sm">- {supportAmount.toLocaleString()}đ</Text></Grid.Col>

                      {otherAdjustment !== 0 && (
                        <>
                          <Grid.Col span={6}><Text size="sm">Điều chỉnh/Khác:</Text></Grid.Col>
                          <Grid.Col span={6} style={{ textAlign: "right" }}>
                            <Text size="sm" c={otherAdjustment < 0 ? "green" : "red"}>
                              {otherAdjustment > 0 ? "+" : ""}{otherAdjustment.toLocaleString()}đ
                            </Text>
                          </Grid.Col>
                        </>
                      )}

                      <Grid.Col span={12}>
                        <Divider my="xs" />
                      </Grid.Col>

                      {estimatedDiff > 0 ? (
                        <>
                          <Grid.Col span={6}><Text size="sm" fw={700} c="blue">Khách bù dự kiến:</Text></Grid.Col>
                          <Grid.Col span={6} style={{ textAlign: "right" }}>
                            <Text size="md" fw={700} c="blue">
                              +{estimatedDiff.toLocaleString()}đ
                            </Text>
                          </Grid.Col>
                        </>
                      ) : (
                        <>
                          <Grid.Col span={6}><Text size="sm" fw={700} c="green">Khách nhận dự kiến:</Text></Grid.Col>
                          <Grid.Col span={6} style={{ textAlign: "right" }}>
                            <Text size="md" fw={700} c="green">
                              {Math.abs(estimatedDiff).toLocaleString()}đ
                            </Text>
                          </Grid.Col>
                        </>
                      )}
                    </Grid>
                  )}
                </Paper>

                {/* Status selection and internal notes */}
                <Box>
                  <Select
                    label="Trạng thái"
                    data={["Mới", "Đã liên hệ", "Đang tư vấn", "Không quan tâm"]}
                    value={editStatus}
                    onChange={setEditStatus}
                    mb="sm"
                  />
                  <Textarea
                    label="Ghi chú nội bộ"
                    placeholder="Nhập ghi chú..."
                    value={editInternalNote}
                    onChange={(e) => setEditInternalNote(e.currentTarget.value)}
                    rows={3}
                  />
                </Box>
              </Stack>
            </Box>

            {/* Pinned Save Button Footer */}
            <Box
              style={{
                borderTop: "1px solid var(--mantine-color-gray-2)",
                padding: "12px 16px",
                backgroundColor: "#ffffff",
                zIndex: 10
              }}
            >
              <Button
                fullWidth
                variant="filled"
                color="blue"
                onClick={handleSaveDetail}
              >
                Lưu
              </Button>
            </Box>
          </Box>
        )}
      </Modal>
    </Box>
  );
};
